/**
 * ReleaseManager.tsx — V4-3-9 / ADR-0029.
 *
 * V6-0-8: rewired from /deliverables to /releases (V6-0-6 shipped the
 * release routes). "Cut release" enabled; delta preview from delta_json.
 *
 * Route: /w/:slug/app/releases (workspace-scoped, requires auth).
 */

import { useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  PageHeader,
  Spinner,
} from '@aigovops/design-system';
import { useWorkspace } from '../lib/workspace';
import { useWorkspaceQuery } from '../hooks/useWorkspaceQuery';
import { queryKeys } from '../lib/query-keys';
import { req } from '../lib/api';

interface ReleaseRow {
  release_id: string;
  release_no: number;
  manifest_json: Array<{ system_id: string; version_id: string; hash: string }>;
  changelog_json: string[] | null;
  canonical_json_sha256: string;
  released_at: string;
  next_attestation_due: string | null;
  released_by_user_id: string;
}

async function listReleases(slug: string): Promise<{ releases: ReleaseRow[] }> {
  const body = await req<{ releases: ReleaseRow[] }>(`/api/w/${encodeURIComponent(slug)}/releases`);
  return { releases: body?.releases ?? [] };
}

export function ReleaseManager() {
  const { workspace } = useWorkspace();
  const q = useWorkspaceQuery(
    (slug) => queryKeys.releases(slug),
    (slug) => listReleases(slug),
  );
  const [cutting, setCutting] = useState(false);

  if (q.loading) return <Spinner label="Loading releases" />;
  if (q.error) return <Alert variant="error">{q.error}</Alert>;

  const releases = q.data?.releases ?? [];

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <PageHeader
          title="Register releases"
          subtitle={`${releases.length} release${releases.length !== 1 ? 's' : ''}`}
        />
        <Button onClick={() => setCutting(true)}>Cut release</Button>
      </div>

      {releases.length === 0 ? (
        <EmptyState
          title="No releases yet"
          description="Cut a release to create a signed manifest of all signed system entries."
        />
      ) : (
        <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
          {releases.map((rel) => (
            <ReleaseCard key={rel.release_id} release={rel} />
          ))}
        </div>
      )}

      {cutting && (
        <CutReleaseDialog
          slug={workspace.slug}
          onClose={() => { setCutting(false); window.location.reload(); }}
        />
      )}
    </div>
  );
}

function ReleaseCard({ release }: { release: ReleaseRow }) {
  const [showDetail, setShowDetail] = useState(false);
  const pastDue = release.next_attestation_due && new Date() > new Date(release.next_attestation_due);

  return (
    <Card>
      <div style={{
        padding: 'var(--space-3) var(--space-4)',
        display: 'grid',
        gap: 'var(--space-2)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
          <span style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 'var(--font-size-md)',
            fontWeight: 700,
            color: 'var(--text)',
          }}>
            #{release.release_no}
          </span>
          <Badge tone={pastDue ? 'neutral' : 'accent'}>
            {pastDue ? 'Refresh due' : 'Current'}
          </Badge>
          <span style={{ flex: 1 }} />
          <span style={{
            fontFamily: 'var(--font-ui)',
            fontSize: 'var(--font-size-3xs)',
            color: 'var(--text-dim)',
          }}>
            {new Date(release.released_at).toLocaleDateString()}
          </span>
          <span style={{
            fontFamily: 'var(--font-ui)',
            fontSize: 'var(--font-size-3xs)',
            color: 'var(--text-dim)',
          }}>
            {release.manifest_json.length} system{release.manifest_json.length !== 1 ? 's' : ''}
          </span>
        </div>

        {release.changelog_json && release.changelog_json.length > 0 && (
          <ul style={{
            margin: 0,
            paddingLeft: 'var(--space-4)',
            fontSize: 'var(--font-size-3xs)',
            color: 'var(--text-muted)',
          }}>
            {release.changelog_json.map((line, i) => (
              <li key={i}>{line}</li>
            ))}
          </ul>
        )}

        <button
          onClick={() => setShowDetail((v) => !v)}
          style={{
            border: 'none',
            background: 'none',
            color: 'var(--accent)',
            fontSize: 'var(--font-size-3xs)',
            fontFamily: 'var(--font-ui)',
            cursor: 'pointer',
            textAlign: 'left',
            padding: 0,
          }}
        >
          {showDetail ? 'Hide details' : 'Show manifest + hash'}
        </button>

        {showDetail && (
          <div style={{
            fontSize: 'var(--font-size-3xs)',
            fontFamily: 'var(--font-mono)',
            color: 'var(--text-dim)',
            background: 'var(--surface-elevated, var(--surface))',
            padding: 'var(--space-2)',
            borderRadius: 'var(--radius-card)',
            overflowX: 'auto',
          }}>
            <div>SHA-256: {release.canonical_json_sha256}</div>
            {release.next_attestation_due && (
              <div>Next attestation: {release.next_attestation_due}</div>
            )}
            <div style={{ marginTop: 'var(--space-1)' }}>
              {release.manifest_json.map((m) => (
                <div key={m.system_id}>
                  {m.system_id} → {m.version_id} ({m.hash.slice(0, 12)}…)
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}

function CutReleaseDialog({ slug, onClose }: { slug: string; onClose: () => void }) {
  const [changelog, setChangelog] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCut = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const changelogLines = changelog.split('\n').filter((l) => l.trim());
      await req(`/api/w/${encodeURIComponent(slug)}/releases`, {
        method: 'POST',
        body: JSON.stringify({ changelog: changelogLines.length > 0 ? changelogLines : undefined }),
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cut release');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'rgba(0,0,0,0.4)', zIndex: 100,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <Card>
        <div style={{ padding: 'var(--space-4)', display: 'grid', gap: 'var(--space-3)', minWidth: 400 }}>
          <h2 style={{ fontFamily: 'var(--font-ui)', fontSize: 'var(--font-size-md)', fontWeight: 600, margin: 0, color: 'var(--text)' }}>
            Cut a new release
          </h2>
          <p style={{ margin: 0, fontFamily: 'var(--font-ui)', fontSize: 'var(--font-size-2xs)', color: 'var(--text-muted)' }}>
            This creates a signed manifest of all signed system entries and notifies subscribers.
          </p>
          <label style={{ fontFamily: 'var(--font-ui)', fontSize: 'var(--font-size-2xs)', color: 'var(--text-muted)' }}>
            Changelog (one line per change)
            <textarea
              value={changelog}
              onChange={(e) => setChangelog(e.target.value)}
              rows={4}
              style={{
                display: 'block', width: '100%', marginTop: 'var(--space-1)',
                padding: 'var(--space-2)', border: '1px solid var(--border)',
                borderRadius: 'var(--radius-card)', background: 'var(--surface)',
                color: 'var(--text)', fontFamily: 'var(--font-ui)',
                fontSize: 'var(--font-size-2xs)', resize: 'vertical',
              }}
            />
          </label>
          {error && <Alert variant="error">{error}</Alert>}
          <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
            <Button onClick={onClose}>Cancel</Button>
            <Button onClick={handleCut} disabled={submitting}>
              {submitting ? 'Cutting…' : 'Cut release'}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
